// 📄 pages/Admin/components/GuiaIntegracionFormModal.tsx
import React, { useState } from 'react';
import { X, Save, Plus, Trash2, ArrowUp, ArrowDown } from 'lucide-react';
import {
  GuiaIntegracion,
  PasoGuia,
  saveGuiaIntegracion,
  generarPasoId
} from '../../../services/guiasIntegracionService.ts';
import { SOLUCION_OPTIONS } from '../../Proyectos/solucionesConfig.ts';
import { TIPOS_ERP } from '../../Proyectos/erpConfig.ts';

interface GuiaIntegracionFormModalProps {
  guia: GuiaIntegracion | null; // null = alta nueva
  onClose: () => void;
  onSaved: () => void;
}

const pasoVacio = (): PasoGuia => ({ id: generarPasoId(), titulo: '', descripcion: '' });

export default function GuiaIntegracionFormModal({ guia, onClose, onSaved }: GuiaIntegracionFormModalProps) {
  const [solucion, setSolucion] = useState(guia?.solucion || SOLUCION_OPTIONS[0].value);
  const [erpTipo, setErpTipo] = useState(guia?.erpTipo || TIPOS_ERP[0].value);
  const [nombre, setNombre] = useState(guia?.nombre || '');
  const [descripcion, setDescripcion] = useState(guia?.descripcion || '');
  const [pasos, setPasos] = useState<PasoGuia[]>(guia?.pasos?.length ? guia.pasos : [pasoVacio()]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const actualizarPaso = (id: string, campo: 'titulo' | 'descripcion', valor: string) => {
    setPasos((prev) => prev.map((p) => (p.id === id ? { ...p, [campo]: valor } : p)));
  };

  const agregarPaso = () => setPasos((prev) => [...prev, pasoVacio()]);
  const quitarPaso = (id: string) => setPasos((prev) => (prev.length > 1 ? prev.filter((p) => p.id !== id) : prev));

  const moverPaso = (index: number, direccion: -1 | 1) => {
    setPasos((prev) => {
      const destino = index + direccion;
      if (destino < 0 || destino >= prev.length) return prev;
      const copia = [...prev];
      [copia[index], copia[destino]] = [copia[destino], copia[index]];
      return copia;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!nombre.trim()) {
      setError('El nombre de la guía es obligatorio.');
      return;
    }
    if (!pasos.some((p) => p.titulo.trim())) {
      setError('Agregá al menos un paso con título.');
      return;
    }

    setSaving(true);
    try {
      await saveGuiaIntegracion(solucion, erpTipo, { nombre, descripcion, pasos }, !guia);
      onSaved();
    } catch (err: any) {
      setError(err.message || 'Error al guardar la guía.');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full bg-zinc-800 border border-white/10 rounded-lg p-2.5 text-sm text-white focus:outline-none focus:border-indigo-500";
  const labelClass = "block text-xs font-medium text-gray-400 mb-1";

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[rgb(var(--c-surface))] border border-white/10 rounded-2xl w-full max-w-2xl shadow-2xl my-8">
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h3 className="text-lg font-medium text-white">
            {guia ? 'Editar Guía de Integración' : 'Nueva Guía de Integración'}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          {error && (
            <div className="bg-red-500/10 border border-red-500/30 text-red-400 p-3 rounded-lg text-xs">{error}</div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Solución</label>
              <select
                className={inputClass}
                value={solucion}
                disabled={!!guia}
                onChange={(e) => setSolucion(e.target.value)}
              >
                {SOLUCION_OPTIONS.map((s) => (
                  <option key={s.value} value={s.value}>{s.icon} {s.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelClass}>Tipo de ERP</label>
              <select
                className={inputClass}
                value={erpTipo}
                disabled={!!guia}
                onChange={(e) => setErpTipo(e.target.value)}
              >
                {TIPOS_ERP.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
          </div>
          {guia && (
            <p className="text-[10px] text-gray-500 -mt-2">
              Solución y ERP no se pueden cambiar una vez creada — borrá esta guía y creá una nueva si necesitás otra combinación.
            </p>
          )}

          <div>
            <label className={labelClass}>Nombre de la guía <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={nombre}
              onChange={(e) => setNombre(e.target.value)}
              placeholder="ej. SLP + S/4HANA Cloud Pública"
              className={inputClass}
            />
          </div>

          <div>
            <label className={labelClass}>Descripción (opcional)</label>
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="De qué se trata esta guía, cuándo usarla..."
              rows={2}
              className={`${inputClass} resize-none`}
            />
          </div>

          <div className="pt-2 border-t border-white/10">
            <div className="flex items-center justify-between mb-2">
              <label className={labelClass}>Pasos del checklist <span className="text-red-400">*</span></label>
              <button
                type="button"
                onClick={agregarPaso}
                className="flex items-center gap-1 text-xs text-indigo-400 hover:text-indigo-300"
              >
                <Plus className="w-3.5 h-3.5" /> Agregar paso
              </button>
            </div>

            <div className="space-y-3">
              {pasos.map((paso, index) => (
                <div key={paso.id} className="bg-white/5 border border-white/10 rounded-xl p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="mt-2.5 text-xs font-mono text-gray-500 w-5 flex-shrink-0">{index + 1}.</span>
                    <div className="flex-1 space-y-2">
                      <input
                        type="text"
                        value={paso.titulo}
                        onChange={(e) => actualizarPaso(paso.id, 'titulo', e.target.value)}
                        placeholder="Título del paso — ej. Habilitar Communication Arrangement SAP_COM_0053"
                        className={inputClass}
                      />
                      <textarea
                        value={paso.descripcion}
                        onChange={(e) => actualizarPaso(paso.id, 'descripcion', e.target.value)}
                        placeholder="Detalle de cómo hacerlo (opcional)"
                        rows={2}
                        className={`${inputClass} resize-none`}
                      />
                    </div>
                    <div className="flex flex-col gap-1 flex-shrink-0">
                      <button type="button" onClick={() => moverPaso(index, -1)} disabled={index === 0} className="text-gray-500 hover:text-white disabled:opacity-20">
                        <ArrowUp className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => moverPaso(index, 1)} disabled={index === pasos.length - 1} className="text-gray-500 hover:text-white disabled:opacity-20">
                        <ArrowDown className="w-3.5 h-3.5" />
                      </button>
                      <button type="button" onClick={() => quitarPaso(paso.id)} disabled={pasos.length === 1} className="text-gray-500 hover:text-red-400 disabled:opacity-20">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white hover:bg-white/5 transition-all"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 px-4 py-2 rounded-lg text-sm font-semibold text-[#fff] transition-all"
            >
              <Save className="w-4 h-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
